'use client'

import { useCallback, useEffect, useState } from 'react'

import { createPortal } from 'react-dom'

import { Anchor } from '@/components/client/seo/anchor'
import type { TocItem } from '@/components/server/storyblok/article/article-types'
import { useTranslation } from '@/hooks/client/use-translation'
import { tw } from '@/lib/utils'

interface ArticleTocProps {
  items: TocItem[]
}

export const ArticleToc = ({ items }: ArticleTocProps) => {
  const { t } = useTranslation()
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [activeSection, setActiveSection] = useState<string | undefined>(undefined)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const handleLinkClick = useCallback((e: React.MouseEvent, sectionId: string) => {
    e.preventDefault()
    setIsMobileOpen(false)

    const target = document.getElementById(sectionId)
    if (!target) return

    // Reveal any skipped ancestor before measuring the final header offset.
    target.scrollIntoView({ block: 'start', behavior: 'instant' })
    const offset = 80
    const top = target.getBoundingClientRect().top + window.scrollY - offset
    // Instant jump (no smooth) to match the old site.
    window.scrollTo({ top, behavior: 'auto' })
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    items.forEach(({ id }) => {
      const target = document.getElementById(id)
      if (!target) return

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(id)
          }
        },
        { rootMargin: '-20% 0px -79% 0px', threshold: 0 }
      )

      observer.observe(target)
      observers.push(observer)
    })

    return () => observers.forEach((obs) => obs.disconnect())
  }, [items])

  if (!items.length) return null

  const tocContent = (
    <div className="p-6 lg:p-0">
      <p className="text-navy/50 mt-0 mb-4 flex items-center gap-2 text-sm font-bold tracking-widest uppercase">
        <span className="bg-lapis inline-block h-4 w-1 rounded-full" />
        {t('client.toc.title', 'Table of Contents')}
      </p>

      <ul className="relative ml-1.5 space-y-1 border-l border-neutral-200">
        {items.map(({ id, title }) => {
          const isActive = activeSection === id
          return (
            <li key={id} className="relative">
              <Anchor
                href={`#${id}`}
                onClick={(e) => handleLinkClick(e, id)}
                data-tracking="ignore"
                className={tw(
                  '-ml-[1px] block rounded-r-md border-l-2 py-1 pr-4 pl-4 text-[1.1rem] transition-all duration-300',
                  isActive
                    ? 'text-lapis bg-lapis/10 border-lapis font-semibold'
                    : 'text-navy/60 hover:text-navy border-transparent hover:border-neutral-300 hover:bg-neutral-50'
                )}
              >
                {title}
              </Anchor>
            </li>
          )
        })}
      </ul>
    </div>
  )

  return (
    <aside className="lg:col-span-4">
      {/* Mobile: portal FAB + drawer to body to escape sticky stacking context */}
      {isMounted &&
        createPortal(
          <>
            {/* Mobile Toggle Button (Floating) */}
            <div className="fixed right-6 bottom-[calc(var(--compare-dock-h,0px)+1.5rem)] z-[1001] lg:hidden">
              <button
                onClick={() => setIsMobileOpen(!isMobileOpen)}
                data-tracking="ignore"
                className="bg-lapis hover:bg-lapis/90 focus:ring-lapis rounded-full p-4 text-white shadow-lg transition-all focus:ring-2 focus:ring-offset-2 focus:outline-none"
                aria-label={t('aria.tableOfContents', 'Table of Contents')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              </button>
            </div>

            {/* Mobile Drawer Backdrop */}
            {isMobileOpen && (
              <div
                className="bg-overlay fixed inset-0 z-[1001] transition-opacity lg:hidden"
                onClick={() => setIsMobileOpen(false)}
              />
            )}

            {/* Mobile Drawer */}
            <nav
              aria-label={t('client.toc.title', 'Table of Contents')}
              inert={!isMobileOpen}
              className={tw(
                'fixed top-0 h-screen lg:hidden',
                'w-72 bg-white',
                'z-[1002] transition-transform duration-300 ease-in-out',
                'overflow-y-auto',
                isMobileOpen ? 'right-0 translate-x-0 shadow-2xl' : 'right-0 translate-x-full shadow-none'
              )}
            >
              {tocContent}
            </nav>
          </>,
          document.body
        )}

      {/* Desktop: static nav in sidebar */}
      <nav aria-label={t('client.toc.title', 'Table of Contents')} className="relative mt-4 hidden lg:block">
        {tocContent}
      </nav>
    </aside>
  )
}
